/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, DialogActions, Grid, Link, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UPDATE_HOMESALE_RESALE_OFFER_FAILURE } from 'modules/authorizations/store/types/homesaleTypes';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { getHomesaleInfo, updateHomesaleResaleOffer } from 'modules/authorizations/store/actions/homesale.actions';
import { showToast } from 'modules/layout/layout.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useMemo, useState } from 'react';
import Spinner from 'modules/common/spinner.component';

const FALL_THROUGH_GUID = '186AB0B2-B37F-4070-9D8E-28BF31A4EA9C';
const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const ResaleOffersCard = props => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);

  const { offerDto, authId, homesaleId, deleteOffer, isReadOnly, enumerations, brokerAgents, refreshOffers } = props;

  const offer = offerDto?.homesaleResaleOffer ?? null;
  const propertyInfo = offerDto?.titleCoAddress ?? null;

  const currentBrokerInfo = useMemo(() => (brokerAgents ? brokerAgents.find(a => a.vendorId === offer?.brokerId) : null), [
    brokerAgents,
    offer?.brokerId
  ]);

  const financingTypes = enumerations?.financingTypes?.filter(s => s.isActive) ?? [];
  const offerStatuses = enumerations?.homesaleOfferStatuses?.filter(s => s.isActive) ?? [];

  const offerDetailsTabs = [{ header: 'Offer Details', order: 1, iconPath: ['fas', 'file-invoice'] }];
  const resaleDetailsTabs = [{ header: 'Closing Details', order: 1, iconPath: ['fas', 'house'] }];
  const buyerDetailsTabs = [{ header: 'Broker Details', order: 1, iconPath: ['fas', 'user'] }];

  const offerDetailsSummaries = [
    {
      header: 'Offer Status',
      accessorKey: 'homesaleOfferStatusId',
      type: 'dropdown-v2',
      options: offerStatuses,
      required: true,
      value: offer?.homesaleOfferStatusId ?? null,
      tab: 1
    },
    { header: 'Buyer', accessorKey: 'buyer', type: 'text', required: false, value: offer?.buyer ?? '', tab: 1 },
    { header: 'Offer Amount', accessorKey: 'offerAmount', type: 'money', required: false, value: offer?.offerAmount ?? '', tab: 1 },
    { header: 'Offer Received Date', accessorKey: 'offerDate', type: 'date', required: false, value: offer?.offerDate ?? null, tab: 1 },
    {
      header: 'Offer Verbally Accepted',
      accessorKey: 'acceptDate',
      type: 'date',
      value: offer?.acceptDate ?? null,
      required: false,
      tab: 1
    },
    {
      header: 'Resale Contract Date',
      accessorKey: 'resaleContractDate',
      type: 'date',
      value: offer?.resaleContractDate ?? null,
      required: false,
      tab: 1
    },
    {
      header: 'Fall Through Resale Date',
      accessorKey: 'fallThroughResaleDate',
      type: 'date',
      value: offer?.fallThroughResaleDate ?? null,
      required: false,
      tab: 1
    },
    {
      header: 'Fall Through Reason',
      accessorKey: 'fallThroughReason',
      type: 'text',
      value: offer?.fallThroughReason ?? '',
      required: false,
      tab: 1
    },
    {
      header: 'Scheduled Resale Close',
      accessorKey: 'scheduledResaleClose',
      type: 'date',
      value: offer?.scheduledResaleClose ?? null,
      required: false,
      tab: 1
    },
    { header: 'Earnest Money', accessorKey: 'earnestMoney', type: 'money', required: false, value: offer?.earnestMoney ?? '', tab: 1 },
    {
      header: 'Financing Type',
      accessorKey: 'financingTypeId',
      type: 'dropdown-v2',
      options: financingTypes,
      required: true,
      value: offer?.financingTypeId ?? null,
      tab: 1
    },
    { header: 'Loan Amount', accessorKey: 'mortgageAmount', type: 'money', required: false, value: offer?.mortgageAmount ?? '', tab: 1 },
    {
      header: 'Subject To (Appraisal)',
      accessorKey: 'subjectTo',
      type: 'dropdown',
      options: ['Yes', 'No'],
      value: offer?.subjectTo ?? '',
      required: false,
      tab: 1
    },
    {
      header: 'Buyer Broker Compensation %',
      accessorKey: 'brokerCommission',
      type: 'decimal',
      value: offer?.brokerCommission ?? '',
      required: false,
      tab: 1
    },
    {
      header: 'Buyer Closing Costs Amount',
      accessorKey: 'buyerClosingCostsAmount',
      type: 'money',
      required: false,
      value: offer?.buyerClosingCostsAmount ?? '',
      tab: 1
    },
    {
      header: 'Buyer Closing Costs %',
      accessorKey: 'byerClosingCostsPercent',
      type: 'decimal',
      required: false,
      value: offer?.byerClosingCostsPercent ?? '',
      tab: 1
    },
    { header: 'Concessions', accessorKey: 'concessions', type: 'money', value: offer?.concessions ?? '', required: false, tab: 1 },
    { header: 'Warranty', accessorKey: 'warranty', type: 'money', value: offer?.warranty ?? '', required: false, tab: 1 },
    { header: 'Repairs (Notes)', accessorKey: 'repairs', type: 'text', value: offer?.repairs ?? '', required: false, tab: 1 },
    { header: 'Repairs Amount', accessorKey: 'repairsAmount', type: 'money', value: offer?.repairsAmount ?? '', required: false, tab: 1 },
    {
      header: 'Inspection Date',
      accessorKey: 'inspectionDate',
      type: 'date',
      value: offer?.inspectionDate ?? null,
      required: false,
      tab: 1
    },
    {
      header: 'Inspection Release Date',
      accessorKey: 'inspectionReleaseDate',
      type: 'date',
      value: offer?.inspectionReleaseDate ?? null,
      required: false,
      tab: 1
    },
    {
      header: 'Loan App',
      accessorKey: 'loanAppDeadline',
      type: 'date',
      required: false,
      value: offer?.loanAppDeadline,
      tab: 1
    },
    {
      header: 'Loan Commit',
      accessorKey: 'loanCommitDeadline',
      type: 'date',
      required: false,
      value: offer?.loanCommitDeadline,
      tab: 1
    },
    {
      header: 'Appraisal',
      accessorKey: 'appraisalDeadline',
      type: 'date',
      required: false,
      value: offer?.appraisalDeadline,
      tab: 1
    },
    {
      header: 'Mortgage Application Date',
      accessorKey: 'mortgageAppDate',
      type: 'date',
      value: offer?.mortgageAppDate ?? null,
      required: false,
      tab: 1
    }
  ];

  const resaleDetailsSummaries = [
    {
      header: 'Closing Warranty',
      accessorKey: 'resaleHomeWarranty',
      type: 'money',
      value: offer?.resaleHomeWarranty ?? '',
      required: false,
      tab: 1
    },
    { header: 'Closing Repairs', accessorKey: 'resaleRepairs', type: 'money', required: false, value: offer?.resaleRepairs ?? '', tab: 1 },
    {
      header: 'Closing Concessions',
      accessorKey: 'resaleConcessions',
      type: 'money',
      required: false,
      value: offer?.resaleConcessions ?? '',
      tab: 1
    },
    { header: 'Gross Sale Price', accessorKey: 'resalePrice', type: 'money', required: false, value: offer?.resalePrice ?? '', tab: 1 },
    {
      header: 'Net (True) Sale Price',
      accessorKey: 'trueSalePrice',
      type: 'money',
      required: false,
      value: offer?.trueSalePrice ?? '',
      tab: 1
    },
    {
      header: 'Actual Resale Close',
      accessorKey: 'actualResaleClose',
      type: 'date',
      value: offer?.actualResaleClose ?? null,
      required: false,
      tab: 1
    },
    // {
    //   header: 'Actual Buyout Close',
    //   accessorKey: 'actualBuyoutCloseDate',
    //   type: 'date',
    //   value: offer?.actualBuyoutCloseDate ?? null,
    //   required: false,
    //   tab: 1
    // }
  ];

  const brokerDetailsSummaries = [
    {
      header: 'Broker Code',
      accessorKey: 'vendorCode',
      type: 'text',
      required: false,
      value: currentBrokerInfo?.vendorCode ?? '',
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Broker Company',
      accessorKey: 'companyName',
      type: 'text',
      required: false,
      value: currentBrokerInfo?.companyName ?? '',
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Broker Phone',
      accessorKey: 'officePhone',
      type: 'text',
      required: false,
      value: currentBrokerInfo?.officePhone ?? '',
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Broker Email',
      accessorKey: 'officeEmail',
      type: 'text',
      required: false,
      value: currentBrokerInfo?.officeEmail ?? '',
      hideInModal: true,
      tab: 1
    }
  ];

  const sendDtoUpdate = async (updatedOffer, updatedAddress) => {
    const dto = {
      homesaleResaleOffer: updatedOffer ?? offer,
      titleCoAddress: updatedAddress ?? propertyInfo
    };
    const resaleId = dto.homesaleResaleOffer?.homesaleResaleOfferId;
    await props.updateHomesaleResaleOffer(authId, homesaleId, resaleId, dto);
    return refreshOffers(resaleId);
  };

  const updateOfferDetailsFields = async (_tab, values) => {
    if (!offer) return false;
    setIsLoading(true);

    const updatedOffer = {
      ...offer,
      buyer: values.buyer,
      buyerName: values.buyerName,
      programTypeId: values.programTypeId,
      offerDate: values.offerDate === '' ? null : values.offerDate,
      offerAmount: values.offerAmount,
      scheduledResaleClose: values.scheduledResaleClose === '' ? null : values.scheduledResaleClose,
      earnestMoney: values.earnestMoney,
      financingTypeId: values.financingTypeId,
      mortgageAmount: values.mortgageAmount,
      subjectTo: values.subjectTo,
      brokerCommission: values.brokerCommission,
      buyerClosingCostsAmount: values.buyerClosingCostsAmount,
      byerClosingCostsPercent: values.byerClosingCostsPercent,
      concessions: values.concessions,
      warranty: values.warranty,
      repairs: values.repairs,
      repairsAmount: values.repairsAmount,
      inspectionDate: values.inspectionDate === '' ? null : values.inspectionDate,
      inspectionReleaseDate: values.inspectionReleaseDate === '' ? null : values.inspectionReleaseDate,
      homesaleOfferStatusId: values.homesaleOfferStatusId,
      loanAppDeadline: values.loanAppDeadline === '' ? null : values.loanAppDeadline,
      loanCommitDeadline: values.loanCommitDeadline === '' ? null : values.loanCommitDeadline,
      appraisalDeadline: values.appraisalDeadline === '' ? null : values.appraisalDeadline,
      mortgageAppDate: values.mortgageAppDate === '' ? null : values.mortgageAppDate,
      resaleContractDate: values.resaleContractDate === '' ? null : values.resaleContractDate,
      fallThroughResaleDate: values.fallThroughResaleDate === '' ? null : values.fallThroughResaleDate,
      fallThroughReason: values.fallThroughReason

    };

    const resp = await sendDtoUpdate(updatedOffer, propertyInfo);
    if (resp.type === UPDATE_HOMESALE_RESALE_OFFER_FAILURE) {
      props.showToast('Failed to update resale offer details.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    }
    await props.getHomesaleInfo(authId);
    setIsLoading(false);
    return true;
  };

  const updateResaleDetailsFields = async (_tab, values) => {
    if (!offer) return false;
    setIsLoading(true);

    const updatedOffer = {
      ...offer,
      acceptDate: values.acceptDate === '' ? null : values.acceptDate,
      mortgageAppDate: values.mortgageAppDate === '' ? null : values.mortgageAppDate,
      resaleHomeWarranty: values.resaleHomeWarranty,
      resaleRepairs: values.resaleRepairs,
      resaleConcessions: values.resaleConcessions,
      resalePrice: values.resalePrice,
      trueSalePrice: values.trueSalePrice,
      actualResaleClose: values.actualResaleClose === '' ? null : values.actualResaleClose,
      comments: values.comments,
      // actualBuyoutCloseDate: values.actualBuyoutCloseDate === '' ? null : values.actualBuyoutCloseDate
    };

    const resp = await sendDtoUpdate(updatedOffer, propertyInfo);
    if (resp.type === UPDATE_HOMESALE_RESALE_OFFER_FAILURE) {
      props.showToast('Failed to update resale closing details.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    }
    await props.getHomesaleInfo(authId);
    setIsLoading(false);
    return true;
  };

  const markSaleFellThrough = async () => {
    if (!offer) return false;
    setIsLoadingButton(true);
    const updatedOffer = { ...offer, fallThroughDate: new Date().toISOString(), homesaleOfferStatusId: FALL_THROUGH_GUID };
    const resp = await sendDtoUpdate(updatedOffer, propertyInfo);
    if (resp.type === UPDATE_HOMESALE_RESALE_OFFER_FAILURE) {
      props.showToast('Failed to mark sale as fallen through.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingButton(false);
      return false;
    }
    setIsLoadingButton(false);
    return true;
  };

  const undoSaleFellThrough = async () => {
    if (!offer) return false;
    setIsLoadingButton(true);
    const updatedOffer = { ...offer, fallThroughDate: null, homesaleOfferStatusId: ZERO_GUID };
    const resp = await sendDtoUpdate(updatedOffer, propertyInfo);
    if (resp.type === UPDATE_HOMESALE_RESALE_OFFER_FAILURE) {
      props.showToast('Failed to undo fall through.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingButton(false);
      return false;
    }
    setIsLoadingButton(false);
    return true;
  };

  const closeSaleNow = async () => {
    if (!offer) return false;
    setIsLoadingButton(true);
    const updatedOffer = { ...offer, actualResaleClose: new Date().toISOString().split('T')[0] };
    const resp = await sendDtoUpdate(updatedOffer, propertyInfo);
    if (resp.type === UPDATE_HOMESALE_RESALE_OFFER_FAILURE) {
      props.showToast('Failed to close sale.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingButton(false);
      return false;
    }
    setIsLoadingButton(false);
    return true;
  };

  const undoCloseSale = async () => {
    if (!offer) return false;
    setIsLoadingButton(true);
    const updatedOffer = { ...offer, actualResaleClose: null };
    const resp = await sendDtoUpdate(updatedOffer, propertyInfo);
    if (resp.type === UPDATE_HOMESALE_RESALE_OFFER_FAILURE) {
      props.showToast('Failed to undo sale close.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingButton(false);
      return false;
    }
    setIsLoadingButton(false);
    return true;
  };

  const offerDetailsButtons = () => (
    <>
      {isLoadingButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        </Box>
      )}
      {!isLoadingButton && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '0 10px' }}>
              {!isReadOnly && offer && (
                <Button
                  onClick={deleteOffer}
                  variant="contained"
                  sx={{ maxHeight: '40px', ml: '12px', mt: '8px', backgroundColor: '#868686', '&:hover': { backgroundColor: '#474747' } }}
                >
                  Delete Offer
                </Button>
              )}
              {!isReadOnly && (
                <Button
                  onClick={markSaleFellThrough}
                  color="secondary"
                  variant="contained"
                  sx={{ maxHeight: '40px', ml: '12px', mt: '8px' }}
                >
                  Sale Fell Through
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
            <Box>
              {offer?.fallThroughDate && (
                <Typography color="secondary">
                  <FontAwesomeIcon size="1x" icon={['fas', 'check-circle']} color="#5D9878" style={{ marginRight: 13 }} />
                  Offer marked as Fall Through.&nbsp;
                  {!isReadOnly && (
                    <>
                      <Link onClick={undoSaleFellThrough} sx={{ cursor: 'pointer' }}>
                        click here
                      </Link>{' '}
                      to undo.
                    </>
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );

  const resaleDetailsButtons = () => (
    <>
      {isLoadingButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        </Box>
      )}
      {!isLoadingButton && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '0 10px' }}>
              {!isReadOnly && !offer?.actualResaleClose && (
                <Button
                  onClick={closeSaleNow}
                  variant="contained"
                  sx={{ maxHeight: '40px', ml: '12px', mt: '8px', backgroundColor: '#868686', '&:hover': { backgroundColor: '#474747' } }}
                >
                  Close Sale
                </Button>
              )}
            </Box>
          </Box>

          {!!offer?.actualResaleClose && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
              <Typography color="secondary">
                <FontAwesomeIcon size="1x" icon={['fas', 'check-circle']} color="#5D9878" style={{ marginRight: 13 }} />
                Resale marked as Closed.&nbsp;
                {!isReadOnly && (
                  <>
                    <Link onClick={undoCloseSale} sx={{ cursor: 'pointer' }}>
                      click here
                    </Link>{' '}
                    to undo.
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
      <Grid item xs={12} xl={8} rowSpacing={2}>
        <Grid item sx={{ mb: '10px' }}>
          <EditableCard
            tabs={offerDetailsTabs}
            summaries={offerDetailsSummaries}
            numColumns={5}
            updateFields={updateOfferDetailsFields}
            isLoading={isLoading}
            ButtonComponent={offerDetailsButtons}
            hideEdit={isReadOnly}
          />
        </Grid>
        <Grid item>
          <EditableCard
            tabs={resaleDetailsTabs}
            summaries={resaleDetailsSummaries}
            numColumns={5}
            updateFields={updateResaleDetailsFields}
            isLoading={isLoading}
            ButtonComponent={resaleDetailsButtons}
            hideEdit={isReadOnly}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} xl={4} rowSpacing={2}>
        <Grid item sx={{ mb: '10px' }}>
          <EditableCard tabs={buyerDetailsTabs} summaries={brokerDetailsSummaries} numColumns={3} isLoading={isLoading} hideEdit={true} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default compose(
  connect(null, {
    updateHomesaleResaleOffer,
    showToast,
    getHomesaleInfo
  })
)(ResaleOffersCard);
